import * as React from 'react';
import { useApolloClient } from '@apollo/client';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/nextjs';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { useRouter } from 'next/router';
import { SIGNUP_COMPLETED_EVENT } from 'constants/analytics';
import { AuthStatus } from 'constants/auth';
import { COACHES_LANDING_PAGE } from 'constants/pages';
import { ErrorResponse, RequestStatus } from 'constants/requests';
import { AccountType, MINIMUM_PASSWORD_LENGTH, getIsValidPasswordFormat } from 'constants/user';
import {
  GetExistingUserQuery,
  SportsEnum,
  useGetClosestCitiesLazyQuery,
  useInsertSignupRequestMutation,
  useUpdateUserDefaultSportMutation,
} from 'types/generated/client';
import { identify, trackEvent } from 'services/client/analytics';
import { facebookTrackCompleteRegistration } from 'services/client/analytics/facebook';
import { auth } from 'services/client/firebase';
import { pollForNewUser } from 'services/client/pollForNewUser';
import { getPlatform } from 'utils/mobile/getPlatform';
import { detectFraudFromBrowser } from 'utils/shared/fraud/detectFraud';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { getSignupErrorMessage } from 'utils/shared/getSignupErrorMessage';
import { splitNameToFirstLast } from 'utils/shared/name/splitNameToFirstLast';
import { validateEmail } from 'utils/shared/validateEmail';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useGetIpAddress } from 'hooks/useGetIpAddress';
import { useViewer } from 'hooks/useViewer';
import { Button, ButtonLinkText } from 'components/Button';
import TabSwitcher from 'components/TabSwitcher';
import CardError from 'components/cards/CardError';
import AddressSearchEnhanced from 'components/forms/AddressSearch/AddressSearchEnhanced';
import { findBestCityMatch } from 'components/forms/FormSignup/utils';
import classNames from 'styles/utils/classNames';
import { getUtmAttributionVariables } from 'utils/client/attribution';

const CITY_DISTANCE_MILES = 100;

const SPORT_OPTIONS = [
  {
    label: 'Pickleball',
    value: SportsEnum.Pickleball,
    iconSrc: '/images/programming/pickleball.png',
  },
  {
    label: 'Tennis',
    value: SportsEnum.Tennis,
    iconSrc: '/images/programming/tennis.png',
  },
  {
    label: 'Padel',
    value: SportsEnum.Padel,
    iconSrc: '/images/programming/padel.png',
  },
];

const boldTextClassName =
  'typography-product-subheading max-lg:typography-product-subheading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary';

interface Props {
  handleSignupSuccess?: ({ userId }: { userId: string }) => void | Promise<void>;
  shouldSkipReload?: boolean;
  handleAuthComplete?: () => void;
}

export default function FormCreateAccountReminder({
  handleSignupSuccess,
  shouldSkipReload,
  handleAuthComplete,
}: Props) {
  const router = useRouter();
  const viewer = useViewer();
  const client = useApolloClient();
  const {
    position,
    activeAutocompleteAddress,
    suggestedAddresses,
    addressString,
    handleSubmitAutocomplete,
    handleAutcompleteSuggestions,
    setAddressString,
    initializeGeoLocation,
    isLoadingAutocomplete,
    autocompleteInputLength,
    autocompleteMinLength,
    isGoogleMapsAvailable,
  } = useGeoLocation();
  const [cityId, setCityId] = React.useState<null | string>(null);
  const [lastValidAddress, setLastValidAddress] = React.useState<null | string>(addressString);
  const { ipResponse } = useGetIpAddress();
  const addressStringRef = React.useRef<string>(addressString || '');
  const [insertSignupRequestQuery] = useInsertSignupRequestMutation();
  const [getClosestCitiesLazyQuery] = useGetClosestCitiesLazyQuery();
  const [updateUserDefaultSportMutation] = useUpdateUserDefaultSportMutation();
  const [primarySport, setPrimarySport] = React.useState<SportsEnum>(SportsEnum.Pickleball);
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [locationError, setLocationError] = React.useState('');
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const [error, setError] = React.useState<ErrorResponse | null>(null);
  const [requestStatus, setRequestStatus] = React.useState(RequestStatus.Idle);
  const isDisabled =
    requestStatus === RequestStatus.InProgress ||
    requestStatus === RequestStatus.Success ||
    viewer.status === AuthStatus.User;

  React.useEffect(() => {
    if (!position) {
      initializeGeoLocation();
    }
  }, []);

  React.useEffect(() => {
    if (router.isReady && position) {
      const addressString = addressStringRef.current;
      getClosestCitiesLazyQuery({
        variables: {
          distance: milesToMeters(CITY_DISTANCE_MILES),
          from: {
            type: 'Point',
            coordinates: [position.longitude, position.latitude],
          },
        },
      }).then((response) => {
        const cities = response?.data?.cities || [];
        const bestMatch = findBestCityMatch(addressString, cities, position);

        if (bestMatch) {
          setCityId(bestMatch.id);
        }
      });
    }
  }, [router.isReady, position]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (requestStatus === RequestStatus.InProgress || requestStatus === RequestStatus.Success) {
      return;
    }

    const fraudDetection = detectFraudFromBrowser();
    if (fraudDetection.isFraud) {
      Sentry.captureException(new Error('Fraud attempt detected during signup'), {
        extra: {
          reason: fraudDetection.reason,
          timezone: fraudDetection.timezone,
          locale: fraudDetection.locale,
          email,
          fullName,
        },
      });
      setError({ message: 'Unable to create account. Please try again later.' });
      setRequestStatus(RequestStatus.Error);
      return;
    }

    setRequestStatus(RequestStatus.InProgress);
    setError(null);
    setEmailError('');
    setPasswordError('');
    setLocationError('');

    const isValidEmail = validateEmail(email);
    const isValidPassword = getIsValidPasswordFormat(password);
    const isValidLocation =
      !!position && !!position.latitude && !!position.longitude && !!addressString;

    if (!isValidEmail) {
      setEmailError('Please enter a valid email address');
    }
    if (!isValidPassword) {
      setPasswordError(
        password.length < MINIMUM_PASSWORD_LENGTH
          ? 'Password must be at least 8 characters'
          : 'Invalid password format',
      );
    }
    if (!isValidLocation) {
      setLocationError('Please enter a valid location');
    }

    if (!isValidEmail || !isValidPassword || !isValidLocation) {
      setRequestStatus(RequestStatus.Idle);
      return;
    }

    try {
      await insertSignupRequestQuery({
        variables: {
          email: email.toLowerCase(),
          username: '',
          fullName,
          preferredName: splitNameToFirstLast(fullName).firstName,
          accountType: AccountType.Player,
          ip: ipResponse?.ip || '',
          country: ipResponse?.country || '',
          region: ipResponse?.region || '',
          city: ipResponse?.city || '',
          fullDetails: ipResponse || null,
          platform: getPlatform(),
          timezone: '',
          zip: '',
          cityId: cityId || null,
          latitude: position?.latitude || null,
          longitude: position?.longitude || null,
          ...getUtmAttributionVariables(),
        },
      });
    } catch (error) {
      Sentry.captureException(error);
      setRequestStatus(RequestStatus.Error);
      // @ts-ignore
      setError(error);
      return;
    }

    const OVERALL_TIMEOUT_MS = 45_000;
    let overallTimeoutId: ReturnType<typeof setTimeout>;
    const overallTimeout = new Promise<never>(
      (_, reject) =>
        (overallTimeoutId = setTimeout(
          () =>
            reject(
              new Error(
                'Account setup timed out. Please refresh the page — your account may already be created.',
              ),
            ),
          OVERALL_TIMEOUT_MS,
        )),
    );

    const signupFlow = async () => {
      try {
        Sentry.addBreadcrumb({ category: 'signup', message: 'Creating Firebase user' });
        const user = await createUserWithEmailAndPassword(auth, email, password);
        setRequestStatus(RequestStatus.Success);

        sendEmailVerification(user.user);

        Sentry.addBreadcrumb({ category: 'signup', message: 'Polling for new user in DB' });
        const databaseUser = (await pollForNewUser(
          client,
          user.user,
        )) as GetExistingUserQuery['users'][0];

        if (databaseUser.id) {
          await updateUserDefaultSportMutation({
            variables: { id: databaseUser.id, defaultSport: primarySport },
          });
        }

        if (handleSignupSuccess && databaseUser.id) {
          await handleSignupSuccess({ userId: databaseUser.id });
        }

        facebookTrackCompleteRegistration({
          email,
          fullName,
          username: databaseUser.username,
          userId: databaseUser.id,
          isCoach: false,
          accountType: AccountType.Player,
          coachStatus: databaseUser.coachStatus,
        });

        identify({
          userId: databaseUser.id,
          email,
          name: fullName,
          additionalUserParams: {
            username: databaseUser.username,
            accountType: AccountType.Player,
          },
        });
        trackEvent(SIGNUP_COMPLETED_EVENT, {
          accountType: AccountType.Player,
          isCoach: false,
        });

        if (!shouldSkipReload) {
          router.reload();
        } else if (handleAuthComplete) {
          handleAuthComplete();
        }
      } finally {
        clearTimeout(overallTimeoutId);
      }
    };

    Promise.race([signupFlow(), overallTimeout]).catch(function (errorResponse) {
      Sentry.captureException(
        errorResponse instanceof Error
          ? errorResponse
          : new Error('Sign up / poll did not reach success state'),
        {
          extra: { originalError: errorResponse },
        },
      );
      setRequestStatus(RequestStatus.Error);
      setError({ message: getSignupErrorMessage(errorResponse) });
    });
  };

  return (
    <form className="flex w-full flex-col items-center px-4 lg:px-10" onSubmit={handleSubmit}>
      <div className="flex w-full flex-col items-center pb-4 pt-16 lg:pb-6 lg:pt-[4.5rem]">
        <h2 className="typography-product-display max-lg:typography-informative-subheading-highlight text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary px-2">
          Make Bounce your weekly racket sports guide
        </h2>
        <p className="typography-product-body max-lg:typography-product-caption mt-2 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary max-lg:px-10">
          Create an <span className={boldTextClassName}>account</span> to get personalized{' '}
          <span className={boldTextClassName}>recommendations</span>,{' '}
          <span className={boldTextClassName}>discounts</span> on programming, and{' '}
          <span className={boldTextClassName}>weekly updates</span> for what&apos;s happening in
          your city.
        </p>
      </div>
      <div className="w-full border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode" />
      <div className="flex w-full flex-col gap-4 py-4 lg:gap-6 lg:py-6">
        <div className="w-full">
          <p className="typography-product-subheading max-lg:typography-product-subheading-mobile mb-2 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            What&apos;s your primary sport?
          </p>
          <div className="flex justify-center">
            <TabSwitcher
              options={SPORT_OPTIONS}
              selectedValue={primarySport}
              onValueChange={setPrimarySport}
              keepIconColor={true}
              wrapperClassName="max-w-[30.5rem]"
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-4">
          <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-x-6">
            <div className="w-full">
              <label
                className="typography-product-subheading max-lg:typography-product-subheading-mobile mb-1 block text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:mb-2"
                htmlFor="reminder-name"
              >
                Name
              </label>
              <input
                id="reminder-name"
                name="name"
                autoComplete="name"
                type="text"
                value={fullName}
                onFocus={() => setError(null)}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Full name"
                disabled={isDisabled}
                className="input-form"
                required
              />
            </div>
            <div className="w-full">
              <label
                className="typography-product-subheading max-lg:typography-product-subheading-mobile mb-1 block text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:mb-2"
                htmlFor="reminder-location"
              >
                Location
              </label>
              <AddressSearchEnhanced
                id="reminder-location"
                name="location"
                autoComplete="off"
                type="text"
                disabled={isDisabled}
                onBlur={() => {
                  if (lastValidAddress && lastValidAddress !== addressString) {
                    setAddressString(lastValidAddress);
                  }
                }}
                onFocus={() => {
                  setError(null);
                  setLocationError('');
                }}
                handleSubmit={(a) => {
                  addressStringRef.current = a?.description || addressString;
                  setLastValidAddress(a?.description || addressString);
                  handleSubmitAutocomplete(a, false);
                }}
                handleAutcompleteSuggestions={handleAutcompleteSuggestions}
                addressString={addressString}
                suggestedAddresses={suggestedAddresses.slice(0, 3)}
                activeAutocompleteAddress={activeAutocompleteAddress}
                className={classNames('input-form', !!locationError && 'text-color-error')}
                placeholder="City, State"
                isLoadingAutocomplete={isLoadingAutocomplete}
                autocompleteInputLength={autocompleteInputLength}
                autocompleteMinLength={autocompleteMinLength}
                isGoogleMapsAvailable={isGoogleMapsAvailable}
              />
              {!!locationError && (
                <div className="mt-2 text-xs text-color-error">{locationError}</div>
              )}
            </div>
          </div>
          <div className="w-full">
            <label
              className="typography-product-subheading max-lg:typography-product-subheading-mobile mb-1 block text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:mb-2"
              htmlFor="reminder-email"
            >
              Email
            </label>
            <input
              id="reminder-email"
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => {
                setError(null);
                setEmailError('');
              }}
              placeholder="sample@gmail.com"
              disabled={isDisabled}
              className={classNames('input-form', !!emailError && 'text-color-error')}
              required
            />
            {!!emailError && <div className="mt-2 text-xs text-color-error">{emailError}</div>}
          </div>
          <div className="w-full">
            <label
              className="typography-product-subheading max-lg:typography-product-subheading-mobile mb-1 block text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:mb-2"
              htmlFor="reminder-password"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="reminder-password"
                name="password"
                autoComplete="new-password"
                type={isShowPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => {
                  setError(null);
                  setPasswordError('');
                }}
                minLength={MINIMUM_PASSWORD_LENGTH}
                disabled={isDisabled}
                className={classNames(
                  'input-form ph-no-autocapture',
                  !!passwordError && 'text-color-error',
                )}
                required
              />
              <div className="absolute right-4 top-3 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                {isShowPassword ? (
                  <button
                    className="h-4 w-4"
                    type="button"
                    onClick={() => setIsShowPassword(false)}
                  >
                    <EyeIcon />
                  </button>
                ) : (
                  <button type="button" onClick={() => setIsShowPassword(true)}>
                    <EyeSlashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {passwordError ? (
              <div className="mt-2 flex items-start gap-1.5 text-xs text-color-error">
                <ExclamationCircleIcon className="mt-px h-4 w-4 shrink-0" />
                <span>{passwordError}</span>
              </div>
            ) : (
              <p className="typography-product-text-card-mobile mt-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Passwords should contain three of the four character types: Uppercase letters: A-Z.
                Lowercase letters: a-z. Numbers: 0-9.
              </p>
            )}
          </div>
          {!!error?.message && <CardError>{error.message}</CardError>}
          <div className="flex justify-center">
            <Button
              variant="brand"
              sizeMobile="md"
              sizeDesktop="lg"
              type="submit"
              disabled={isDisabled}
              isInline
            >
              {requestStatus === RequestStatus.InProgress
                ? 'Creating account...'
                : 'Create free account'}
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode" />
      <div className="typography-product-body max-lg:typography-product-caption mt-4 flex items-center justify-center gap-2 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:mt-6">
        <InformationCircleIcon className="h-5 w-5 shrink-0 text-color-text-brand" />
        Bounce is free. No credit card required.
      </div>
      <div className="mt-4 pb-6 text-center lg:mt-6 lg:pb-8">
        <ButtonLinkText
          href={COACHES_LANDING_PAGE}
          size="md"
          className="inline-flex items-center gap-1.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
        >
          <img src="/images/app/cap-colored.png" className="h-3.5 w-3.5" alt="" />
          I&apos;m a coach
        </ButtonLinkText>
      </div>
    </form>
  );
}
